/**
 * Full-screen error overlay — the thing that stands between a crash and a white screen.
 *
 * A WebGPU or WebGL failure otherwise shows up as a black canvas and a console message nobody
 * sees. This catches `error` and `unhandledrejection`, plus anything start-up throws, and puts it
 * on screen where it cannot be missed.
 *
 * ## Why there is a copy button
 *
 * Because the alternative is asking somebody to retype a stack trace, or to find and open a
 * developer console they have never opened, on the day their simulator broke. What gets copied is
 * not only the stack: it is the stack **plus the environment** — which renderer initialised, which
 * quality tier, the URL with its query string, and the user agent. Every one of those has been the
 * actual answer to a bug at some point in this project, and none of them are in the exception.
 *
 * The clipboard can refuse: it needs a secure context and, in some browsers, a permission. So the
 * failure path selects the text instead, turning the button into a way to *reach* the details
 * rather than a way to fail at copying them.
 *
 * ## And why a WebGL2 button rather than a sentence about a query parameter
 *
 * `?forcewebgl=1` is the first thing to try for anything graphics-shaped, and it was previously
 * printed as a URL for the reader to retype. A button that reloads into it is the same information
 * with the work already done.
 */

import { PRODUCT_NAME } from '../brand.js';

export interface ErrorOverlayOptions {
  /**
   * Extra lines describing the running system, gathered at the moment of the failure.
   *
   * A callback rather than a value: the overlay is constructed before the renderer exists, and
   * what a report most needs to say is which backend actually initialised.
   */
  context?: () => readonly string[];
  parent?: HTMLElement;
}

export class ErrorOverlay {
  readonly element: HTMLDivElement;

  private readonly options: ErrorOverlayOptions;
  private readonly body: HTMLPreElement;
  private readonly copyButton: HTMLButtonElement;
  private readonly webglButton: HTMLAnchorElement;
  private readonly subtitle: HTMLParagraphElement;
  private shown = false;
  /** Exactly what the copy button copies — the displayed report, environment and all. */
  private report = '';

  constructor(options: ErrorOverlayOptions | HTMLElement = {}) {
    // Accepts a bare parent for the sake of the original two-year-old call site in `main.ts`.
    this.options = options instanceof HTMLElement ? { parent: options } : options;
    const parent = this.options.parent ?? document.body;

    this.element = document.createElement('div');
    this.element.className = 'error-overlay';
    this.element.hidden = true;

    const card = document.createElement('div');
    card.className = 'error-overlay__card';

    const title = document.createElement('h1');
    title.className = 'error-overlay__title';
    title.textContent = `${PRODUCT_NAME} hit an error`;

    this.subtitle = document.createElement('p');
    this.subtitle.className = 'error-overlay__subtitle';
    this.subtitle.textContent =
      'Copy the details below if you are reporting this — they include which renderer and tier were running.';

    this.body = document.createElement('pre');
    this.body.className = 'error-overlay__body';

    const actions = document.createElement('div');
    actions.className = 'error-overlay__actions';

    this.copyButton = document.createElement('button');
    this.copyButton.type = 'button';
    this.copyButton.className = 'error-overlay__button error-overlay__button--primary';
    this.copyButton.textContent = 'COPY DETAILS';
    this.copyButton.addEventListener('click', () => void this.copy());

    /*
     * An anchor rather than a button, and that is deliberate.
     *
     * Whatever went wrong may well have left the page in a state where a scripted reload does not
     * happen — a broken renderer, an exhausted heap, a half-initialised module graph. A plain link
     * is navigation the browser performs itself, with no JavaScript of ours in the path, which is
     * the property you want from the one control on a crash screen.
     */
    this.webglButton = document.createElement('a');
    this.webglButton.className = 'error-overlay__button';
    this.webglButton.textContent = 'RELOAD ON WEBGL2';
    this.webglButton.hidden = true;

    const reload = document.createElement('a');
    reload.className = 'error-overlay__button';
    reload.textContent = 'RELOAD';
    reload.href = globalThis.location?.href ?? '/';

    actions.append(this.copyButton, this.webglButton, reload);
    card.append(title, this.subtitle, this.body, actions);
    this.element.append(card);
    parent.append(this.element);
  }

  /** Start catching global errors. */
  install(): void {
    globalThis.addEventListener('error', this.onError);
    globalThis.addEventListener('unhandledrejection', this.onRejection);
  }

  uninstall(): void {
    globalThis.removeEventListener('error', this.onError);
    globalThis.removeEventListener('unhandledrejection', this.onRejection);
  }

  /** Show `error`. The first error wins: later ones are usually fallout from the first. */
  show(error: unknown, context?: string): void {
    if (this.shown) return;
    this.shown = true;

    const parts: string[] = [];
    if (context !== undefined) parts.push(context, '');

    if (error instanceof Error) {
      parts.push(`${error.name}: ${error.message}`);
      if (error.stack !== undefined) parts.push('', error.stack);
    } else {
      parts.push(String(error));
    }

    const text = parts.join('\n');
    /*
     * Also on a global, where a machine can read it.
     *
     * The overlay puts the error where a *person* cannot miss it, which is the right answer when
     * there is a person. A `--dedicated` perf run has no person: the browser is a throwaway profile
     * nobody sees, and an exception inside the frame callback kills the loop permanently and
     * silently — the loop requests its next frame at the end of `onFrame`, so a throw anywhere in
     * the frame is the last frame. `tools/perf.mjs` reads this over DevTools when a run stops
     * reporting, which is the difference between "no result after 255 s" and a stack trace.
     *
     * Deliberately the bare error, without the environment block below: the runner prints this
     * verbatim into a terminal, and the stack is the part it needs.
     */
    (globalThis as { __SKYSIM_LAST_ERROR__?: string }).__SKYSIM_LAST_ERROR__ = text;

    // --- the environment, which is the half a bug report is usually missing ------------------
    parts.push('', '--- environment ---');
    for (const line of this.options.context?.() ?? []) parts.push(line);
    parts.push(`url: ${globalThis.location?.href ?? '(none)'}`);
    parts.push(`agent: ${globalThis.navigator?.userAgent ?? '(unknown)'}`);

    if (looksGraphicsRelated(text)) {
      const url = new URL(globalThis.location?.href ?? 'http://localhost/');
      url.searchParams.set('forcewebgl', '1');
      this.webglButton.href = url.toString();
      this.webglButton.hidden = false;
      this.subtitle.textContent =
        'This looks like a graphics failure. The WebGL2 fallback is the first thing to try.';
    }

    this.report = parts.join('\n');
    this.body.textContent = this.report;
    this.element.hidden = false;
  }

  private async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.report);
      this.copyButton.textContent = 'COPIED';
    } catch {
      // Refused — an insecure origin, or a browser that wants a permission. Select the report so
      // the platform's own copy shortcut can take it; the button becomes a way to reach the text.
      this.copyButton.textContent = 'PRESS ⌘C / CTRL+C';
      const selection = globalThis.getSelection?.();
      if (selection === null || selection === undefined) return;
      const range = document.createRange();
      range.selectNodeContents(this.body);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  private readonly onError = (event: ErrorEvent): void => {
    this.show(event.error ?? event.message, 'Uncaught error');
  };

  private readonly onRejection = (event: PromiseRejectionEvent): void => {
    this.show(event.reason, 'Unhandled promise rejection');
  };
}

export function looksGraphicsRelated(text: string): boolean {
  return /webgpu|webgl|adapter|device|gpu|shader|context/i.test(text);
}
